import React, { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import styles from "./ShapeLibraryModal.module.css";
import { useLanguage } from "../../../context/LanguageContext";

interface ShapeLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectShape: (shapeType: string, shapeName: string) => void;
}

export default function ShapeLibraryModal({
  isOpen,
  onClose,
  onSelectShape,
}: ShapeLibraryModalProps) {
  const { t } = useLanguage();

  const shapes = [
    { type: "primitive", nameKey: "shapeProcess", key: "1" },
    { type: "condition", nameKey: "shapeCondition", key: "2" },
    { type: "loop", nameKey: "shapeLoop", key: "3" },
    { type: "break", nameKey: "shapeBreak", key: "4" },
    { type: "function", nameKey: "shapeFunction", key: "5" },
    { type: "return", nameKey: "shapeReturn", key: "6" },
    { type: "throw", nameKey: "shapeThrow", key: "7" },
    { type: "subtransaction", nameKey: "shapeSubtransaction", key: "8" },
    { type: "event", nameKey: "shapeEvent", key: "9" },
  ];

  const shapeIcons: Record<string, React.ReactNode> = {
    primitive: (
      <svg width="48" height="36" viewBox="0 0 48 36">
        <rect
          x="2"
          y="2"
          width="44"
          height="32"
          fill="none"
          stroke="#333"
          strokeWidth="2"
          rx="2"
        />
      </svg>
    ),
    condition: (
      <svg width="48" height="40" viewBox="0 0 48 40">
        <polygon
          points="24,2 46,20 24,38 2,20"
          fill="none"
          stroke="#333"
          strokeWidth="2"
        />
      </svg>
    ),
    loop: (
      <svg width="48" height="36" viewBox="0 0 48 36">
        <polygon
          points="10,0 38,0 48,18 38,36 10,36 0,18"
          fill="none"
          stroke="#333"
          strokeWidth="2"
        />
      </svg>
    ),
    break: (
      <svg width="48" height="36" viewBox="0 0 48 36">
        <rect
          x="2"
          y="2"
          width="44"
          height="32"
          fill="none"
          stroke="#333"
          strokeWidth="2"
          rx="16"
        />
      </svg>
    ),
    function: (
      <svg width="48" height="36" viewBox="0 0 48 36">
        <rect
          x="2"
          y="2"
          width="44"
          height="32"
          fill="none"
          stroke="#333"
          strokeWidth="2"
          rx="2"
        />
        <line x1="2" y1="9" x2="46" y2="9" stroke="#333" strokeWidth="1.5" />
        <line x1="2" y1="27" x2="46" y2="27" stroke="#333" strokeWidth="1.5" />
      </svg>
    ),
    return: (
      <svg width="48" height="36" viewBox="0 0 48 36">
        <rect
          x="2"
          y="2"
          width="44"
          height="32"
          fill="none"
          stroke="#333"
          strokeWidth="2"
          rx="8"
        />
        <path
          d="M 18 18 L 10 18 M 10 18 L 14 14 M 10 18 L 14 22"
          stroke="#333"
          strokeWidth="2"
          fill="none"
        />
      </svg>
    ),
    throw: (
      <svg width="48" height="40" viewBox="0 0 48 40">
        <polygon
          points="14,2 34,2 46,10 46,30 34,38 14,38 2,30 2,10"
          fill="none"
          stroke="#333"
          strokeWidth="2"
        />
      </svg>
    ),
    event: (
      <svg width="48" height="36" viewBox="0 0 48 36">
        <polygon
          points="12,4 40,18 12,32"
          fill="none"
          stroke="#333"
          strokeWidth="2"
          strokeLinejoin="round"
        />
      </svg>
    ),
    subtransaction: (
      <svg width="48" height="36" viewBox="0 0 48 36">
        <rect
          x="2"
          y="2"
          width="44"
          height="32"
          fill="none"
          stroke="#333"
          strokeWidth="2"
          rx="2"
        />
        <rect
          x="6"
          y="6"
          width="36"
          height="24"
          fill="none"
          stroke="#333"
          strokeWidth="1.5"
          rx="1"
        />
      </svg>
    ),
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      const key = e.key;
      const shape = shapes.find((s) => s.key === key);

      if (shape) {
        e.preventDefault();
        onSelectShape(shape.type, '');
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [isOpen, onSelectShape, onClose]);

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2>{t("selectShape")}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className={styles.content}>
          <div className={styles.shapeGrid}>
            {shapes.map((shape) => (
              <div
                key={shape.type}
                className={styles.shapeCard}
                onClick={() => {
                  onSelectShape(shape.type, '');
                  onClose();
                }}
              >
                <div className={styles.shapeKeyBadge}>{shape.key}</div>
                <div className={styles.shapeIcon}>{shapeIcons[shape.type]}</div>
                <div className={styles.shapeName}>{t(shape.nameKey)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
